import { array, bool, element, func, number, string } from 'prop-types'
import React, { useState } from 'react'

import { useClickOutside } from '../../../../components/shared/hooks/useClickOutside'
import AButtonStandard from '../../button/AButtonStandard'
import Cont from '../../layout/Cont'
import AIcon, { ICONS } from '../../media/icon/AIcon'
import AInput from '../AInput'
import TypeaheadHelper, {
  HELP_TYPE_ERROR,
  HELP_TYPE_HINT,
} from './TypeaheadHelper'
import TypeaheadResults from './TypeaheadResults'
import { findSearchTermInResults } from './TypeaheadUtils'

const MIN_SELECTED_INPUT = -1

const TypeaheadCmp = ({
  currSearch,
  showHelper,
  helperErrorMsg,
  allQuickSearchResults,
  inputFieldMaxLength = 100,
  searchTriggerCmpLabel = 'search button',
  searchTriggerCmpRef = 'search-button',
  onSearchTrigger,
  resetOnSelect,
  onSearchResultItemSelectRequest,
  helperHintMsg,
  showSearchTrigger,
  actionElement,
  onSearchTermChange,
  placeHolderMsg = '',
  searchProviderId,
}) => {
  const [isOpen, setOpen] = useState(false)
  const [selectedInput, setSelectedInput] = useState(MIN_SELECTED_INPUT)
  const [searchString, setSearchString] = useState(currSearch)
  const [helperContent, setHelperContent] = useState(null)
  const [helperContentType, setHelperContentType] = useState(null)

  const onChange = (e) => {
    const { value } = e.target
    onSearchTermChange(value)
    setSearchString(value)
    setSelectedInput(-1)
    setOpen(true)
    setHelperContent(null)
    setHelperContentType(null)
  }

  const onSubmit = (e) => {
    e?.preventDefault()
    e?.stopPropagation()

    const selectedItem =
      allQuickSearchResults[selectedInput] ||
      findSearchTermInResults(searchString, allQuickSearchResults, currSearch)
    const selectedSearchTerm =
      allQuickSearchResults[selectedInput]?.name || searchString
    const newInputSearchTerm = resetOnSelect ? '' : selectedItem?.name

    if (!showHelper || !helperErrorMsg) {
      setSearchString(newInputSearchTerm)
      onSearchTrigger(selectedSearchTerm, selectedItem)
    } else if (selectedSearchTerm) {
      setHelperContent(helperErrorMsg)
      setHelperContentType(HELP_TYPE_ERROR)
    } else {
      setSearchString(newInputSearchTerm)
      setOpen(false)
      onSearchTrigger(selectedSearchTerm, selectedItem)
    }
  }

  const onKeyDown = (e) => {
    if (e.key === 'ArrowUp') {
      setSelectedInput((selectedInput) =>
        Math.max(MIN_SELECTED_INPUT, selectedInput - 1),
      )
    } else if (e.key === 'ArrowDown') {
      const maxSelectedInput = allQuickSearchResults.length - 1
      setSelectedInput((selectedInput) =>
        Math.min(maxSelectedInput, selectedInput + 1),
      )
    }
  }

  const onFocus = () => {
    if (!showHelper || !helperHintMsg || !searchString) return

    setHelperContent(helperHintMsg)
    setHelperContentType(HELP_TYPE_HINT)
  }

  const onItemSelect = (selectedItem) => {
    selectedItem.searchString = searchString
    setSearchString(resetOnSelect ? '' : selectedItem.name)
    setOpen(false)
    onSearchResultItemSelectRequest(selectedItem)
  }

  const onClickOutside = () => {
    setOpen(false)
    setHelperContent(null)
    setHelperContentType(null)
  }

  const { containerRef } = useClickOutside({ onClickOutside })

  return (
    <Cont formGroup className={'custom-searchbar'} ref={containerRef}>
      <div className="searchbar" onKeyUp={onKeyDown}>
        <form className="searchForm" onSubmit={onSubmit}>
          <div className="searchbar__controls">
            <div className="searchbar__input">
              <AInput
                maxLength={inputFieldMaxLength}
                type="text"
                placeholder={placeHolderMsg}
                onClick={onFocus}
                onChange={onChange}
                value={searchString}
                cmpRef={'searchbar-input'}
              />
            </div>
            {showSearchTrigger && (
              <div className="searchbar__button">
                <AButtonStandard
                  onClick={onSubmit}
                  cmpLabel={searchTriggerCmpLabel}
                  successButton={true}
                  style="primary"
                  size="large"
                  cmpRef={searchTriggerCmpRef}
                >
                  <AIcon icon={ICONS.search} size={'03x'} />
                </AButtonStandard>
              </div>
            )}
            {!showSearchTrigger && !!actionElement && (
              <div className="searchbar__button">{actionElement}</div>
            )}
          </div>
          <TypeaheadResults
            allQuickSearchResults={allQuickSearchResults}
            onSearchResultItemSelectRequest={onItemSelect}
            selectedIndex={selectedInput}
            show={isOpen}
            searchString={searchString}
            cmpRef={'search-result'}
            searchProviderId={searchProviderId}
          />
          <TypeaheadHelper
            content={helperContent}
            contentType={helperContentType}
          />
        </form>
      </div>
    </Cont>
  )
}

TypeaheadCmp.propTypes = {
  onSearchTrigger: func.isRequired,
  onSearchTermChange: func.isRequired,
  allQuickSearchResults: array,
  onSearchResultItemSelectRequest: func.isRequired,
  currSearch: string,
  showHelper: bool,
  helperErrorMsg: string,
  helperHintMsg: string,
  placeHolderMsg: string,
  inputFieldMaxLength: number,
  resetOnSelect: bool,
  actionElement: element,
  showSearchTrigger: bool,
  searchTriggerCmpLabel: string,
  searchTriggerCmpRef: string,
  searchProviderId: string,
}

export default TypeaheadCmp
